import { FriendlyCaptchaSDK } from '@friendlycaptcha/sdk';

// --- SDK Singleton ---
let friendlySdk = null;
export function getFriendlySdk () {
    if (!friendlySdk) {
        friendlySdk = new FriendlyCaptchaSDK();
    }
    return friendlySdk;
}

// --- Helpers (dev reachability) ---
function withTimeout (promise, ms, label) {
    let t;
    const timeout = new Promise((resolve, reject) => {
        t = setTimeout(() => reject(new Error(`${label || 'timeout'} after ${ms}ms`)), ms);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(t));
}

function shouldRunDevChecks () {
    try {
        if (typeof window !== 'undefined' && window.FRC_DEV_CHECK === true) return true;
        const host = (typeof location !== 'undefined' && location.hostname) ? location.hostname : '';
        return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
    } catch (_) {
        return false;
    }
}

export async function devReachabilityChecks ({ apiEndpoint, assetsUrl, puzzleEndpoint }) {
    if (!shouldRunDevChecks()) return;
    const urls = [];
    if (apiEndpoint) urls.push(apiEndpoint);
    if (assetsUrl) urls.push(assetsUrl);
    if (puzzleEndpoint) urls.push(puzzleEndpoint);
    const unique = Array.from(new Set(urls.filter(Boolean)));
    for (const url of unique) {
        try {
            const controller = new AbortController();
            const p = fetch(url, { method: 'HEAD', mode: 'no-cors', signal: controller.signal });
            await withTimeout(p, 2000, `HEAD ${url}`);
            // eslint-disable-next-line no-console
            console.debug('[FRC][dev] Reachability OK:', url);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[FRC][dev] Reachability problem:', url, err && err.message ? err.message : err);
        }
    }
}

// --- DOM helpers ---
export function findCaptchaMount (root) {
    if (!root) return null;
    return root.querySelector ? root.querySelector('.frc-captcha') : null;
}

// --- Options resolver ---
export function resolveOptionsFromMount (mount) {
    if (!mount) return null;
    const sitekey = mount.getAttribute('data-sitekey');
    if (!sitekey) return null;

    const apiEndpointAttr = mount.getAttribute('data-api-endpoint') || 'global';
    const assetsUrl = mount.getAttribute('data-assets-url');

    const options = {
        sitekey,
        element: mount
    };

    // Endpoint normalisieren: bevorzugt data-api-endpoint, sonst Origin aus data-puzzle-endpoint
    const resolvedEndpoint = apiEndpointAttr || null;
    if (resolvedEndpoint) options.apiEndpoint = resolvedEndpoint;
    if (assetsUrl) options.assetsUrl = assetsUrl;

    return { options };
}

function attachDefaultListeners (widget, label) {
    console.log('attached');
    const prefix = label ? `[FRC][${label}]` : '[FRC]';
    if (!widget || typeof widget.addEventListener !== 'function') return;
    widget.addEventListener('frc:widget.complete', function (event) {
        // eslint-disable-next-line no-console
        console.log(`${prefix} Widget complete:`, event.detail && event.detail.response);
    });
    widget.addEventListener('frc:widget.error', function (event) {
        // eslint-disable-next-line no-console
        console.error(`${prefix} Widget error:`, event.detail);
    });
    widget.addEventListener('frc:widget.expire', function () {
        // eslint-disable-next-line no-console
        console.warn(`${prefix} Widget expired`);
    });
}

// --- Public API: init or re-init for a given root ---
export function initOrReinitFriendlyCaptcha ({ root, prevWidget = null, label = '' } = {}) {
    const mount = findCaptchaMount(root);
    if (!mount) {
        // eslint-disable-next-line no-console
        console.error('[FRC] Captcha element not found.');
        return { widget: null, mount: null, options: null };
    }

    const resolved = resolveOptionsFromMount(mount);
    console.log(resolved);
    if (!resolved) {
        // eslint-disable-next-line no-console
        console.error('[FRC] Missing FriendlyCaptcha sitekey');
        return { widget: null, mount, options: null };
    }

    const { options } = resolved;

    // Diagnose-Logs
    try {
        // eslint-disable-next-line no-console
        console.debug('[FRC] Captcha dataset:', { ...mount.dataset });
        // eslint-disable-next-line no-console
        console.debug('[FRC] Resolved options:', { sitekey: options.sitekey, apiEndpoint: options.apiEndpoint, assetsUrl: options.assetsUrl });
    } catch (_) {}

    // Orphan iframes entfernen
    const iframes = mount.querySelectorAll('iframe');
    if (iframes.length > 0) {
        // eslint-disable-next-line no-console
        console.debug(`[FRC] Cleaning ${iframes.length} iframe(s) before widget creation`);
        iframes.forEach((node) => node.remove());
    }

    // Dev reachability
    devReachabilityChecks({ apiEndpoint: options.apiEndpoint, assetsUrl: options.assetsUrl });
    let widget = null;

    if (!widget) {
        const sdk = getFriendlySdk();
        widget = sdk.createWidget(options);
        attachDefaultListeners(widget, label);
    }

    return { widget, mount, options };
}
